import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import * as admin from '../../api/admin.ts';
import { toast } from '../../store/toast.ts';

type Draft = Record<string, number | boolean>;

export function SettingsTab() {
  const queryClient = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ['admin-settings'], queryFn: admin.settings });
  const [draft, setDraft] = useState<Draft>({});

  useEffect(() => {
    if (!data) return;
    setDraft(Object.fromEntries(data.items.map((s) => [s.key, s.value])));
  }, [data]);

  const save = useMutation({
    mutationFn: () => admin.updateSettings(draft),
    onSuccess: (r) => {
      if (r.applied.length === 0) {
        toast.error('적용된 항목이 없습니다. 값의 범위를 확인해 주세요');
        return;
      }
      toast.success(`${r.applied.length}개 설정을 저장했습니다`);
      void queryClient.invalidateQueries({ queryKey: ['admin-settings'] });
    },
    onError: (e: Error) => { toast.error(e); },
  });

  if (isLoading || !data) return <span className="spinner" />;

  const dirty = data.items.some((s) => draft[s.key] !== s.value);

  return (
    <section className="panel">
      <h2>운영 정책</h2>
      <p className="panel__hint">
        저장하면 30초 안에 모든 요청과 백그라운드 잡에 반영됩니다.
      </p>

      {data.items.map((setting) => (
        <div key={setting.key} className="panel__row">
          <div className="spacer" style={{ minWidth: 0 }}>
            <div style={{ fontSize: 13, fontWeight: 500 }}>{setting.label}</div>
            <div className="faint" style={{ fontSize: 12 }}>{setting.description}</div>
            <div className="faint mono" style={{ fontSize: 11 }}>{setting.key}</div>
          </div>

          {setting.type === 'bool' ? (
            <input
              className="checkbox" type="checkbox"
              checked={draft[setting.key] === true}
              aria-label={setting.label}
              onChange={(e) => { setDraft({ ...draft, [setting.key]: e.target.checked }); }}
            />
          ) : (
            <input
              className="input" type="number" style={{ width: 120 }}
              min={setting.min} max={setting.max}
              aria-label={setting.label}
              value={String(draft[setting.key] ?? setting.value)}
              onChange={(e) => { setDraft({ ...draft, [setting.key]: Number(e.target.value) }); }}
            />
          )}
        </div>
      ))}

      <div className="row" style={{ marginTop: 16 }}>
        <button
          className="btn btn--primary" disabled={!dirty || save.isPending}
          onClick={() => { save.mutate(); }}
        >
          {save.isPending ? <span className="spinner" /> : '저장'}
        </button>
        {dirty && (
          <button
            className="btn"
            onClick={() => {
              setDraft(Object.fromEntries(data.items.map((s) => [s.key, s.value])));
            }}
          >
            되돌리기
          </button>
        )}
      </div>
    </section>
  );
}
